import React from "react";
import { ArrowUpRight } from "lucide-react";

const SECTORS = [
  {
    tag: "Schools",
    title: "Calmer classrooms, softer transitions, safer engagement.",
    body:
      "A consistent, structured canine presence helps guarded students settle, supports difficult transitions, and quietly strengthens the protective factors that shape learning.",
    img: "https://images.pexels.com/photos/35758721/pexels-photo-35758721.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  },
  {
    tag: "Veteran Communities",
    title: "Grounded presence at the gatherings that matter.",
    body:
      "At remembrance events, support groups, and veteran-serving facilities, our partners offer a steadying presence. We honor the Sentinel 22 — disciplined, humble, never performative.",
    img: "https://images.pexels.com/photos/13711974/pexels-photo-13711974.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  },
  {
    tag: "Rural Facilities",
    title: "Structured calm where resources stretch thin.",
    body:
      "Across rural Alaska and beyond, deliberate presence interrupts accumulated stress in facilities, clinics, and community spaces — without adding to the load on local staff.",
    img: "https://images.unsplash.com/photo-1683444066011-bb456c79d79d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  },
  {
    tag: "Behavioral Health & Youth",
    title: "A non-clinical adjunct to the people doing the work.",
    body:
      "We support — never replace — therapists, counselors, and behavioral specialists. We stabilize the rooms in which their care is delivered, by design and by doctrine.",
    img: "https://images.pexels.com/photos/6451234/pexels-photo-6451234.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  },
];

export default function WhoWeServe() {
  return (
    <section data-testid="who-we-serve" className="bg-white">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="brand-rule" />
              <span className="overline">Who we serve</span>
            </div>
            <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
              Built for the complex systems where escalation costs the most.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-12">
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              We work where the stakes are operational and human at once — places where a
              single shift in tension changes outcomes for dozens of people.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16">
          {SECTORS.map((s, i) => (
            <article
              key={s.tag}
              data-testid={`sector-card-${i}`}
              className="group"
            >
              <div className="overflow-hidden border border-slate-200">
                <img
                  src={s.img}
                  alt={s.tag}
                  loading="lazy"
                  className="w-full h-72 md:h-80 object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                />
              </div>
              <div className="mt-6 flex items-baseline justify-between gap-4">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
                  {s.tag}
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-slate-400 group-hover:text-amber-600 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </div>
              <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-slate-900 leading-tight">
                {s.title}
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
